import {LabelItem, UnsavedLabelItem} from '~redux/types/images';
import {InspectionSubType, InspectionType, Model} from '~redux/types/models';

import {ANOMALY, CLASS_EMPTY, CLASS_NOK, CLASS_OK, LABEL_ID_SEPARATOR} from 'src/constants/constants';
import {theme} from 'src/constants/theme';
import {
  AnomalyIcon,
  ClassificationIcon,
  DataCodesIcon,
  KeypointDetectionIcon,
  LegacyModelIcon,
  MeasurementIcon,
  ModelIcon,
  ObjectDetectionIcon,
  RuleBasedDetectionIcon,
  SurfaceInspectionIcon,
} from 'src/icons';

/**
 * Return the icon for a given InspectionType. Defaults to the generic model icon.
 */
export function getInspectionTypeIcon(type: InspectionType | undefined): JSX.Element {
  switch (type) {
    case InspectionType.surfaceInspection:
      return <SurfaceInspectionIcon />;
    case InspectionType.classification:
      return <ClassificationIcon />;
    case InspectionType.objectDetection:
      return <ObjectDetectionIcon />;
    case InspectionType.legacyModel:
      return <LegacyModelIcon />;
    case InspectionType.anomalyDetection:
      return <AnomalyIcon />;
    case InspectionType.keypointDetection:
      return <KeypointDetectionIcon />;
    case InspectionType.ruleBased:
      return <RuleBasedDetectionIcon />;
    case InspectionType.measurement:
      return <MeasurementIcon />;
    case InspectionType.dataCodes:
      return <DataCodesIcon />;
    default:
      return <ModelIcon />;
  }
}

/**
 * Return the translation key (namespace `models`) of the "minimum images for training" hint message
 * for a given inspection type. Keypoint detection shares the generic message with the fallback types.
 */
export function getMinImagesHintMessageKey(
  inspectionType: InspectionType | undefined,
  inspectionSubType: InspectionSubType | undefined,
): string {
  switch (inspectionType) {
    case InspectionType.surfaceInspection:
      return 'minImagesHint.message.defect';
    case InspectionType.classification:
      return inspectionSubType === InspectionSubType.defect
        ? 'minImagesHint.message.defect'
        : 'minImagesHint.message.feature';
    case InspectionType.anomalyDetection:
      return 'minImagesHint.message.anomaly';
    case InspectionType.objectDetection:
      return 'minImagesHint.message.objectDetection';
    default:
      return 'minImagesHint.message.default';
  }
}

/**
 * Retrieve a list of predefined labels for the given inspection type.
 */
export function getPredefinedLabels(
  inspectionType: InspectionType | undefined,
  inspectionSubType: InspectionSubType | undefined,
): UnsavedLabelItem[] {
  switch (inspectionType) {
    case InspectionType.surfaceInspection:
      return [
        {predefined: true, alias: CLASS_OK, colorCode: theme.palette.maddox.classOk, type: 'class', condition: 'OK'},
      ];
    case InspectionType.objectDetection:
      return [
        {
          predefined: true,
          alias: CLASS_EMPTY,
          colorCode: theme.palette.maddox.classEmpty,
          type: 'class',
          condition: 'OTHER',
        },
      ];
    case InspectionType.anomalyDetection:
      return [
        {predefined: true, alias: CLASS_OK, colorCode: theme.palette.maddox.classOk, type: 'class', condition: 'OK'},
        {
          predefined: true,
          alias: ANOMALY,
          colorCode: '#f44336',
          type: 'polygon',
          condition: 'NOK',
        },
      ];
    case InspectionType.measurement:
      return [
        {predefined: true, alias: CLASS_OK, colorCode: theme.palette.maddox.classOk, type: 'class', condition: 'OK'},
        {
          predefined: true,
          alias: CLASS_NOK,
          colorCode: '#f44336',
          type: 'class',
          condition: 'NOK',
        },
      ];
    case InspectionType.classification:
      return inspectionSubType === InspectionSubType.feature
        ? []
        : [
            {
              predefined: true,
              alias: CLASS_OK,
              colorCode: theme.palette.maddox.classOk,
              type: 'class',
              condition: 'OK',
            },
          ];
    default:
      return [];
  }
}

/**
 * Return the only NOK class of a binary Defect Classification model, i.e. the class the
 * "Classify NOK and save" hotkey assigns. Returns undefined for any other inspection type or when
 * the model has zero or several NOK classes (deleted labels are ignored).
 */
export function getSingleNokClassLabel(model: Model | null | undefined): LabelItem | undefined {
  if (
    !model ||
    model.inspectionType !== InspectionType.classification ||
    model.inspectionSubtype !== InspectionSubType.defect
  ) {
    return undefined;
  }
  const nokLabels = model.labels.filter(
    (label) => !label.deleted && label.type === 'class' && label.condition === 'NOK',
  );
  return nokLabels.length === 1 ? nokLabels[0] : undefined;
}

/**
 * Determine if a given label is a predefined label of the given inspection type
 * @see {@link getPredefinedLabels} to see the predefined labels.
 */
export function isPredefinedLabel(
  labelId: string | undefined,
  inspectionType: InspectionType,
  inspectionSubType: InspectionSubType | undefined,
): boolean {
  if (labelId === undefined) {
    return false;
  }

  const labelAliasPart = labelId.split(LABEL_ID_SEPARATOR)[0];
  const predefinedLabels = getPredefinedLabels(inspectionType, inspectionSubType);
  return predefinedLabels.some((predefinedLabel) => predefinedLabel.alias === labelAliasPart);
}

export function getInspectionType(labelId: string, models: Model[]): InspectionType | undefined {
  const model = models.find((model) => {
    return model.labels.find((label) => label.id === labelId);
  });
  return model?.inspectionType;
}

export function getInspectionSubType(labelId: string, models: Model[]): InspectionSubType | undefined {
  const model = models.find((model) => {
    return model.labels.find((label) => label.id === labelId);
  });
  return model?.inspectionSubtype;
}

export function getModelName(modelId?: string, models?: Model[]): string | undefined {
  const model = models?.find((model) => model.id === modelId);
  return model?.name;
}

export function getModelInspectionType(modelId?: string, models?: Model[]): InspectionType | undefined {
  const model = models?.find((model) => model.id === modelId);
  return model?.inspectionType;
}

export function getLatestPublishedVersion(modelId?: string, models?: Model[]): string | undefined {
  const model = models?.find((model) => model.id === modelId);
  return model?.latestPublishedVersion;
}

/**
 * Calculates the performance metrics from a multi-class confusion matrix.
 * Note:
 *  - Hits and misses are only computed for classification and return -1 for the other inspection types.
 *  - some metrics may return NaN if the given matrix is invalid and/or division by zero occurs.
 */
export function calculateClassificationMetrics(confusionCells?: ConfusionMatrix['confusionCells']) {
  let hits = 0,
    misses = 0,
    total = 0;

  if (confusionCells) {
    confusionCells.forEach((cell) => {
      const count = cell.count ?? 0;
      total += count;

      if (cell.actualClass === cell.predictedClass) {
        // Hit
        hits += count;
      } else {
        // Miss
        misses += count;
      }
    });
  }

  return {hits, misses, total};
}
